<template>
  <div class="w-full flex flex-col items-center relative">
    <div
      class="top-5 cursor-pointer bg-grayscale-13 rounded-full items-center justify-center w-8 h-8 hidden lg:absolute lg:flex left-4 z-50"
      @click="goBack"
    >
      <i class="icon-arrow-left text-base text-grayscale-96" />
    </div>
    <div
      class="w-full max-w-1120 px-6 grid gap-x-16 gap-y-4 grid-cols-1 lg:grid-cols-3 mt-8 lg:mt-16"
    >
      <div class="col-span-1 lg:col-span-2">
        <name-job :pro="pro" class="w-full" />
      </div>
      <div
        v-if="pro.profileVideo"
        class="relative w-full my-8 lg:my-0 lg:row-span-3"
      >
        <image-kit
          video
          autoplay
          width="640"
          height="640"
          loop
          :muted="muted"
          playsinline
          :data-wf-ignore="true"
          data-object-fit="cover"
          :path="pro.profileVideo"
          :alt="`Video of ${pro?.firstName} ${pro?.lastName}`"
          class="object-cover rounded-12 w-full h-64 lg:w-[327px] lg:h-[473px]"
        />
        <i
          :class="muted ? 'icon-volume_slash' : 'icon-audio'"
          class="p-2 rounded-full absolute top-4 right-4 cursor-pointer border border-l-grayscale-96"
          @click="mute"
        />
      </div>
      <div v-else class="hidden lg:block row-span-3">
        <image-kit
          width="327"
          height="473"
          data-object-fit="cover"
          :path="pro.profilePicture || '/images/placeholder.png'"
          :alt="`Selfie of ${pro?.firstName} ${pro?.lastName}`"
          class="shrink-0 w-[327px] h-[473px] rounded-12 object-cover"
        />
      </div>
      <div class="col-span-1 lg:col-span-2 lg:-mt-4">
        <div
          class="border-t border-grayscale-20 lg:px-4 py-4 rounded-b-12 lg:bg-grayscale-10"
        >
          <pro-availability
            :pro="pro"
            class="py-2 w-full"
            @scroll-to-products="scrollToProducts"
          />
        </div>
      </div>
      <div class="flex flex-col lg:col-span-2">
        <div
          v-if="pro.canHelpWith || pro.connections?.length"
          class="px-6 py-8 colored-box relative flex flex-col gap-8 mt-9"
        >
          <div
            class="w-5 h-5 bg-[#16143A] rotate-45 absolute -top-[10px] border-[#635BFF] border-t border-l"
          />
          <div v-if="pro.canHelpWith" class="flex flex-col gap-4 z-10">
            <p class="text-lg font-bold">Here's how I can help</p>
            <p class="text-15 leading-6">
              {{ pro.canHelpWith }}
            </p>
          </div>
          <div v-if="pro.connections?.length" class="flex flex-col gap-4 z-10">
            <p class="text-14 font-bold">I helped these artists:</p>
            <industry-info :pro="pro"></industry-info>
          </div>
        </div>
        <products
          :pro="pro"
          :feedback-price="minFeedback?.price.toString()"
          :call-price="minCall?.price.toString()"
          :play-transition="connect"
          :top-button-request="buttonRequest"
          :loading="isLoading"
          class="mt-16 mb-8"
        />

        <div class="flex flex-col md:flex-row gap-4 md:gap-6">
          <div class="text-grayscale-96 flex items-center">
            <i class="icon-shield-tick leading-4 mr-3" /><span
              class="text-xs leading-140 shrink-0"
              >Secure payment & money back guarantee.</span
            >
          </div>
          <div class="flex gap-1">
            <p
              v-for="pay in payMethods"
              class="w-9 h-6 bg-grayscale-16 rounded-2 flex items-center justify-center"
            >
              <image-kit :path="`/card-icons/${pay}.svg`" alt="interscope" />
            </p>
          </div>
        </div>

        <bio v-if="pro.bio" :pro="pro" class="mt-12"></bio>
        <div class="mb-8 overflow-hidden mt-20">
          <div class="mb-8 overflow-hidden">
            <HowItWorks class="w-full" />
          </div>
          <Testimonials
            v-if="testimonials.length"
            class="w-full"
            title="Testimonials"
            :testimonials="testimonials"
          />
          <FAQ class="mt-20" :items="sessionDataStore.faqs" />
        </div>
      </div>
      <div
        v-if="breadcrumbs.length"
        class="flex flex-wrap items-center gap-4 py-2 border-t border-b border-grayscale-16 col-span-1 lg:col-span-3 mt-20 lg:mt-36"
      >
        <div
          class="flex gap-4 items-center"
          v-for="(breadcrumb, ind) in breadcrumbs"
          :key="breadcrumb.name"
        >
          <router-link
            class="hover:text-accent-orange"
            :to="`${breadcrumb.link}`"
          >
            {{ breadcrumb.name }}
          </router-link>
          <div
            v-if="ind !== breadcrumbs.length - 1"
            class="w-0.5 h-0.5 rounded-full bg-grayscale-50"
          ></div>
        </div>
      </div>
    </div>
    <ProCarousel
      v-if="related.job"
      :pros="related.related"
      :title="`other ${related.job}s`"
    />
  </div>
  <Footer />
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";
import IndustryInfo from "../components/profile/industry-pro/IndustryInfo.vue";
import NameJob from "../components/profile/industry-pro/NameJob.vue";

import Products from "../components/profile/industry-pro/Products.vue";
import { ProductType } from "@/types/product";
import type { Product } from "@/types/product";
import { useAuthenticationStore } from "@/store/authentication";

import { useProStore } from "@/store/pros";
import PageTitles from "@/assets/strings";
import { useProAvailabilityStore } from "@/store/pro-availability";
import ImageKit from "@/components/imagekit/ImageKit.vue";
import { getImageKitUrl } from "@/utils/imageKit";
import { useUserStore } from "../store/user";
import ProAvailability from "@/components/profile/industry-pro/ProAvailability.vue";
import Bio from "@/components/profile/industry-pro/Bio.vue";
import { slugify } from "@/types/directory";
import { City } from "@/types/user";
import ProCarousel from "@/components/layout/ProCarousel.vue";
import Footer from "@/components/footers/Footer.vue";
import Testimonials from "@/components/layout/Testimonials.vue";
import HowItWorks from "@/components/layout/HowItWorks.vue";
import { parseProductType } from "@/utils/products";
import dayjs from "dayjs";
import FAQ from "@/components/layout/FAQ.vue";
import { useSessionDataStore } from "~/store/session-data";

const router = useRouter();

const sessionDataStore = useSessionDataStore();
const proStore = useProStore();
const authStore = useAuthenticationStore();
const userStore = useUserStore();
const proAvailabilityStore = useProAvailabilityStore();
const isLoading = ref(false);

const related = computed(() => proStore.related);

const testimonials = computed(() => {
  return proStore.pro.ratings.map((rating) => {
    const isCall = [ProductType.longCall, ProductType.shortCall].includes(
      rating.productType,
    );
    const links = [];
    if (isCall) {
      if (
        proStore.pro.products!.find((el) =>
          [ProductType.longCall, ProductType.shortCall].includes(el.type),
        )
      ) {
        links.push({
          label: "Book a call",
          link: `/purchase/${proStore.pro.uniqueName}?productType=videocall`,
        });
      }
    } else {
      if (
        proStore.pro.products!.find((el) =>
          [ProductType.written, ProductType.audio, ProductType.video].includes(
            el.type,
          ),
        )
      ) {
        links.push({
          label: "Request feedback",
          link: `/purchase/${proStore.pro.uniqueName}?productType=feedback`,
        });
      }
    }
    return {
      _id: rating.orderId,
      name: rating.artistName,
      stars: rating.stars,
      productType: parseProductType(rating.productType),
      date: dayjs(rating.date).format("LL"),
      message: rating.message,
      links,
    };
  });
});

try {
  await proStore.getByUniqueName(
    router.currentRoute.value.params.uniqueName as string,
  );
} catch (err: any) {
  showError({
    statusCode: 404,
    statusMessage: "Pro Not Found",
  });
}

const pro = computed(() => proStore.pro);

if (pro.value.uniqueName != router.currentRoute.value.params.uniqueName) {
  navigateTo(pro.value.uniqueName, { redirectCode: 301 });
}

const searchMinPrice = (types: ProductType[]) => {
  const prices = pro.value?.products
    ?.filter((product) => types.includes(product.type) && product.isAvailable)
    .sort((a, b) => a.price - b.price);
  return prices;
};

const minCall = ref<Product>();
const minFeedback = ref<Product>();
const buttonRequest = ref<ProductType>();
const connect = ref(false);
const isLogged = computed(() => Boolean(authStore.authUser?.authToken));
const soldOutFeedbacks = ref(false);
const soldOutCalls = ref(false);
const muted = ref(true);

const payMethods = [
  "g-pay",
  "apple-pay",
  "visa_white",
  "Mastercard_white",
  "dinnersclub",
  "american-express",
];

onMounted(async () => {
  await proStore.getRelatedPros(
    router.currentRoute.value.params.uniqueName as string,
  );

  isLoading.value = true;
  await proAvailabilityStore.getProCalendarAvailability(
    pro.value,
    userStore.userData.timezone,
  );

  const callPrices = searchMinPrice([
    ProductType.shortCall,
    ProductType.longCall,
  ]);
  if (callPrices) {
    minCall.value = callPrices[0];
  }

  const feedbackPrices = searchMinPrice([
    ProductType.video,
    ProductType.audio,
    ProductType.written,
  ]);
  if (feedbackPrices) {
    minFeedback.value = feedbackPrices[0];
  }

  soldOutFeedbacks.value = Boolean(
    pro.value?.feedBackCount !== undefined &&
      pro.value?.feedBacksPerWeek !== undefined &&
      pro.value?.feedBackCount >= pro.value?.feedBacksPerWeek,
  );
  soldOutCalls.value = Boolean(
    pro.value?.consultationCallCount !== undefined &&
      pro.value?.consultationCallsPerWeek !== undefined &&
      pro.value?.consultationCallCount >= pro.value?.consultationCallsPerWeek,
  );

  isLoading.value = false;
});

const mute = () => {
  muted.value = !muted.value;
};

const goBack = () => {
  if (router.options.history.state.back) {
    router.back();
  } else {
    router.push("/");
  }
};

const scrollToProducts = () => {
  const products = document.getElementById("products");
  if (products) {
    products.scrollIntoView({
      behavior: "smooth",
      block: "start",
      inline: "nearest",
    });
    const calls = document.getElementById("calls");
    const feedbacks = document.getElementById("feedbacks");
    if (calls) {
      calls.classList.add("bg-grayscale-20");
      setTimeout(() => {
        calls.classList.remove("bg-grayscale-20");
      }, 700);
    }
    if (feedbacks) {
      feedbacks.classList.add("bg-grayscale-20");
      setTimeout(() => {
        feedbacks.classList.remove("bg-grayscale-20");
      }, 700);
    }
  }
};

const breadcrumbs = computed(() => {
  const breadcrumbs: { name: string; link: string }[] = [];

  breadcrumbs.push(
    ...(pro.value.job?.map((job) => {
      return {
        name: job,
        link: `/industry-pros/${slugify(job)}`,
      };
    }) ?? []),
  );

  breadcrumbs.push(
    ...(pro.value.genres?.map((genre) => {
      return {
        name: genre,
        link: `/industry-pros/${slugify(genre)}`,
      };
    }) ?? []),
  );

  if (pro.value.city && pro.value.city in City) {
    breadcrumbs.push({
      name: pro.value.city,
      link: `/industry-pros/${slugify(pro.value.city)}`,
    });
  }

  return breadcrumbs;
});

useHead({
  title: PageTitles.proProfile.title
    .replace(
      new RegExp("%PRO%", "g"),
      pro.value?.firstName + " " + pro.value?.lastName,
    )
    .replace("%ONELINER%", `${pro.value?.worksAt}`),
  meta: [
    {
      name: "description",
      content: PageTitles.proProfile.description
        .replace("%PRO%", pro.value?.firstName + " " + pro.value?.lastName)
        .replace("%ONELINER%", `${pro.value?.worksAt}`),
    },
    {
      name: "og:description",
      content: PageTitles.proProfile.description
        .replace("%PRO%", pro.value?.firstName + " " + pro.value?.lastName)
        .replace("%ONELINER%", `${pro.value?.worksAt}`),
    },
    {
      name: "twitter:description",
      content: PageTitles.proProfile.description
        .replace("%PRO%", pro.value?.firstName + " " + pro.value?.lastName)
        .replace("%ONELINER%", `${pro.value?.worksAt}`),
    },
    {
      property: "og:image",
      content: getImageKitUrl(pro.value?.profilePicture || "", {
        format: "png",
      }),
    },
    {
      property: "twitter:image",
      content: getImageKitUrl(pro.value?.profilePicture || "", {
        format: "png",
      }),
    },
    {
      property: "og:image:secure_url",
      content: getImageKitUrl(pro.value?.profilePicture || "", {
        format: "png",
      }),
    },
    {
      property: "og:image:alt",
      content: PageTitles.proProfile.title
        .replace(
          new RegExp("%PRO%", "g"),
          pro.value?.firstName + " " + pro.value?.lastName,
        )
        .replace("%ONELINER%", `${pro.value?.worksAt}`),
    },
    {
      property: "og:image:type",
      content: "image/svg",
    },
    {
      property: "og:image:width",
      content: "512",
    },
    {
      property: "og:image:height",
      content: "512",
    },
    {
      property: "og:title",
      content: PageTitles.proProfile.title
        .replace(
          new RegExp("%PRO%", "g"),
          pro.value?.firstName + " " + pro.value?.lastName,
        )
        .replace("%ONELINER%", `${pro.value?.worksAt}`),
    },
    {
      property: "twitter:title",
      content: PageTitles.proProfile.title
        .replace(
          new RegExp("%PRO%", "g"),
          pro.value?.firstName + " " + pro.value?.lastName,
        )
        .replace("%ONELINER%", `${pro.value?.worksAt}`),
    },
  ],
});
</script>

<style scoped>
.carousel-padding {
  padding-left: 24px;
  padding-right: 24px;
}

@media (min-width: 768px) {
  .carousel-padding {
    padding-left: calc((100vw - 1152px) / 2);
    padding-right: calc((100vw - 1152px) / 2);
  }
}

.bg-blur {
  box-shadow: inset 0px -80px 40px -60px #050505;
}

.swiper-slide {
  width: auto;
}

.colored-box::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: 8px;
  padding: 1px;
  background: linear-gradient(
    90.15deg,
    rgba(99, 91, 255, 1) -8.43%,
    rgba(220, 65, 251, 1) 83.79%,
    rgba(255, 112, 150, 1) 97.59%
  );
  -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
}

.colored-box {
  position: relative;
  background: linear-gradient(
    90deg,
    #16143a 9.48%,
    #350f3c 86.95%,
    #38171f 99.95%
  );
  border-radius: 8px;
}

.colored-box > p {
  background: -webkit-linear-gradient(
    90.15deg,
    rgba(99, 91, 255, 1) -8.43%,
    rgba(220, 65, 251, 1) 83.79%,
    rgba(255, 112, 150, 1) 97.59%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}
</style>
